import { useMutation, useQuery } from "@apollo/client/react";
import { faSoapNote } from "@awesome.me/kit-0572d5a008/icons/kit/custom";
import { alert } from "@components/common/Alert";
import PremiumFeatureGate from "@components/common/premium/PremiumFeatureGate";
import {
  DSButton,
  DSCardWrapper,
  DSCircularButton,
} from "@components/design-system/molecules";
import { DSNoteDisplay } from "@components/design-system/organisms";
import {
  faArrowLeft,
  faFile,
  faUser,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { GET_AUTO_TEMPLATES } from "@graphql/queries/AutoTemplates";
import { useAuth, usePlainNoteEditor, useTemplates } from "@hooks";
import { TextareaAutosize } from "@mui/base";
import { stripNbsp } from "@src/utils";
import { useState } from "react";
import {
  useParams,
  useNavigate,
  useSearchParams,
} from "react-router";

import { UpdateTeamDefaultButton } from "../UpdateTeamDefaultButton";
import { CopyTemplateModal } from "./CopyTemplateModal";
import ErrorState from "./ErrorState";
import LoadingState from "./LoadingState";
import TemplateCTABanner from "./TemplateCTABanner";
import { COPY_AUTO_TEMPLATE } from "../../../graphql/mutations/AutoTemplates";
import { GET_AUTO_TEMPLATE_BY_UUID_NO_AUTH } from "../../../graphql/queries/AutoTemplates";
import { trackOperation } from "../../../utils/operationTracking";

const FIELD_TO_DATA_KEY = {
  SUBJECTIVE: "subjective",
  OBJECTIVE: "objective",
  ASSESSMENT: "assessment",
  PLAN: "plan",
  CUSTOM: "content",
};

const SharedTemplatePage = () => {
  const { templateUuid } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loading, error, data, refetch } = useQuery(
    GET_AUTO_TEMPLATE_BY_UUID_NO_AUTH,
    {
      variables: { uuid: templateUuid },
    },
  );
  const { isUserAuthenticated } = useAuth();
  const { refetchDefaultTemplates } = useTemplates();
  const usePlainEditor = usePlainNoteEditor();
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [customTemplateName, setCustomTemplateName] = useState("");

  const [copyAutoTemplate, { loading: isCopying }] = useMutation(
    COPY_AUTO_TEMPLATE,
    {
      refetchQueries: [GET_AUTO_TEMPLATES],
    },
  );

  const template = data?.autoTemplateById;

  const noteDisplayData = {};
  (template?.fields || []).forEach((field) => {
    if (field) {
      const key = FIELD_TO_DATA_KEY[field.fieldName];
      if (key) {
        noteDisplayData[key] = field.fieldValue || "";
      }
    }
  });

  const handleCopyTemplate = async () => {
    setShowCopyModal(false);

    await trackOperation("template-copied", async (ctx) => {
      ctx.addProperties({
        sourceTemplateUuid: templateUuid,
        customName: customTemplateName,
      });

      const { data } = await copyAutoTemplate({
        variables: {
          templateUuid,
          name: customTemplateName || undefined,
        },
      });

      if (data?.copyAutoTemplate?.ok) {
        ctx.addProperties({
          newTemplateUuid: data?.copyAutoTemplate?.templateUuid,
        });
        alert(
          "success",
          <h5 className="text-sm font-normal text-white">
            Template copied successfully! To use this template,{" "}
            <a
              onClick={() => {
                navigate("/dashboard");
              }}
              className="underline cursor-pointer hover:opacity-80"
            >
              click here
            </a>{" "}
            and select it when creating a recording.
          </h5>,
        );
        navigate(
          `/templates/edit/${data?.copyAutoTemplate?.templateUuid}`,
        );
      }
    });
  };

  const buildReturnTo = () =>
    `${window.location.pathname}${window.location.search}`;

  const prepareAuthRedirect = () => {
    const target = buildReturnTo();
    // Temporarily suppress onboarding so users can return to the template; cleared on logout
    localStorage.setItem("onboardingSkipped", "true");
    return target;
  };

  const handleSignUp = () => {
    navigate(
      `/auth/register?next=${encodeURIComponent(
        prepareAuthRedirect(),
      )}`,
    );
  };

  const handleSignIn = () => {
    navigate(
      `/auth/login?next=${encodeURIComponent(prepareAuthRedirect())}`,
    );
  };

  const handleBackClick = () => {
    const next = searchParams.get("next");
    if (next) {
      navigate(decodeURIComponent(next));
    } else {
      navigate("/templates");
    }
  };

  if (loading) return <LoadingState />;
  if (error || !template) return <ErrorState />;

  const showCtaBanner = !isUserAuthenticated;

  return (
    <div className={showCtaBanner ? "pb-24 md:pb-28" : ""}>
      <CopyTemplateModal
        shouldShow={showCopyModal}
        hideModal={() => setShowCopyModal(false)}
        confirmAction={handleCopyTemplate}
        templateName={template.name}
        customName={customTemplateName}
        onNameChange={setCustomTemplateName}
      />

      <div className="bg-primary rounded-large my-md">
        <div className="flex items-start justify-between mb-sm">
          <div className="flex items-center space-x-4">
            {isUserAuthenticated && (
              <DSCircularButton
                icon={faArrowLeft}
                onClick={handleBackClick}
                aria-label="Back"
                dataTestId="back-button"
                size="sm"
              />
            )}
            <div className="shrink-0">
              <FontAwesomeIcon
                icon={
                  template.templateType === "SOAP"
                    ? faSoapNote
                    : faFile
                }
                className="text-4xl text-brand"
              />
            </div>
            <div className="flex flex-col gap-xs">
              <h2>{template.name}</h2>
              <div className="flex items-center text-sm text-secondary">
                <span className="flex items-center">
                  <FontAwesomeIcon icon={faUser} className="mr-1" />
                  Created by {template.user?.firstName}{" "}
                  {template.user?.lastName}
                </span>
              </div>
              {template.description && (
                <p className="text-secondary">
                  {template.description}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-3 shrink-0">
            {isUserAuthenticated ? (
              <>
                <PremiumFeatureGate
                  featureName="Adaptive Templates"
                  iconPosition="right"
                >
                  <DSButton
                    onClick={() => setShowCopyModal(true)}
                    type="button"
                    dataTestId="copy-template-button"
                    disabled={isCopying}
                  >
                    {isCopying
                      ? "Copying..."
                      : "Copy to My Templates"}
                  </DSButton>
                </PremiumFeatureGate>
                <>
                  <UpdateTeamDefaultButton
                    template={template}
                    onSuccess={() => {
                      refetch();
                      refetchDefaultTemplates();
                    }}
                    source="shared template page"
                  />
                </>
              </>
            ) : null}
          </div>
        </div>
      </div>

      <DSCardWrapper
        variant="ghost"
        padding="p-lg"
        dataTestId="template-structure-card"
      >
        <h2 className="text-lg font-semibold text-primary mb-md">
          Template Structure
        </h2>
        {template.fields &&
          template.fields.length > 0 &&
          (usePlainEditor ? (
            <div className="space-y-2">
              {(template.fields || []).map((field) => {
                if (!field) return null;
                const label =
                  field.fieldName.charAt(0) +
                  field.fieldName.slice(1).toLowerCase();
                return (
                  <div key={field.fieldName}>
                    <p className="text-xs text-secondary mb-1">
                      {label === "Custom" ? "Content" : label}
                    </p>
                    <TextareaAutosize
                      className="w-full text-xs md:text-sm p-3 border border-input rounded-standard bg-input text-primary resize-none"
                      minRows={3}
                      value={stripNbsp(field.fieldValue || "")}
                      readOnly
                    />
                  </div>
                );
              })}
            </div>
          ) : (
            <DSNoteDisplay
              data={noteDisplayData}
              variant={
                template.templateType === "SOAP" ? "soap" : "freeform"
              }
              layout="stacked"
              showAdditionalNotes={false}
              dataTestId="shared-template-note-display"
            />
          ))}

        {template.userPreferences && (
          <div className="mt-lg">
            <h3>Template Preferences</h3>
            <p className="text-base text-secondary whitespace-pre-wrap">
              {stripNbsp(template.userPreferences)}
            </p>
          </div>
        )}
      </DSCardWrapper>

      {showCtaBanner && (
        <TemplateCTABanner
          handleSignIn={handleSignIn}
          handleSignUp={handleSignUp}
        />
      )}
    </div>
  );
};

export default SharedTemplatePage;
